import api from './api';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface FieldUpdateEstimateResponse {
  kb_id: string;
  field_name: string;
  estimated_chunks: number;
  estimated_clusters: number;
  estimated_cost_cents: number;
  currency: string;
}

export interface FieldUpdateLaunchRequest {
  kb_id: string;
  intent?: string;
  gate_threshold?: number;
  match_threshold?: number;
}

export interface FieldUpdateLaunchResponse {
  run_id: string;
  status: 'running';
}

export type ClusterCategory = 'novel' | 'reinforcing' | 'outlier';

export interface EvidenceQuote {
  doc_id: string;
  page_number: number | null;
  chunk_index: number | null;
  quote: string;
}

export interface IdeaCluster {
  cluster_id: string;
  category: ClusterCategory;
  cluster_name: string;
  description: string;
  chunk_count: number;
  doc_count: number;
  supporting_doc_ids: string[];
  evidence_quotes: EvidenceQuote[];
  reinforces_solution: string | null;
  similarity_to_known: number | null;
}

export interface IdeaLandscape {
  run_id: string;
  sub_project_id: string;
  field_name: string;
  kb_id: string;
  total_chunks_gated: number;
  total_clusters: number;
  novel_count: number;
  reinforcing_count: number;
  outlier_count: number;
  clusters: IdeaCluster[];
  gate_threshold: number;
  match_threshold: number;
}

export interface FieldUpdateRewriteRequest {
  run_id: string;
  selected_cluster_ids: string[];
  refinement_instruction?: string;
}

export interface FieldUpdateRewriteResponse {
  run_id: string;
  proposed_text: string;
  citation_count: number;
  llm_model_used: string | null;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const fieldUpdateService = {
  async estimate(
    subProjectId: string,
    fieldName: string,
    kbId: string,
  ): Promise<FieldUpdateEstimateResponse> {
    const res = await api.post(
      `/sub-projects/${subProjectId}/field-update/${fieldName}/estimate`,
      { kb_id: kbId },
    );
    return res.data;
  },

  async launch(
    subProjectId: string,
    fieldName: string,
    data: FieldUpdateLaunchRequest,
  ): Promise<FieldUpdateLaunchResponse> {
    const res = await api.post(
      `/sub-projects/${subProjectId}/field-update/${fieldName}`,
      data,
    );
    return res.data;
  },

  /** Single poll — returns IdeaLandscape if done, null if still running, throws on error. */
  async _pollOnce(
    subProjectId: string,
    fieldName: string,
    runId: string,
  ): Promise<IdeaLandscape | null> {
    try {
      const res = await api.get(
        `/sub-projects/${subProjectId}/field-update/${fieldName}/run/${runId}`,
      );
      if (res.status === 200) return res.data as IdeaLandscape;
      return null;
      } catch (err: unknown) {
      if ((err as { response?: { status?: number } })?.response?.status === 202) return null;
      throw err;
    }
  },

  /** Poll until complete (202 = still running). Rejects with the server error on failure. */
  async pollUntilDone(
    subProjectId: string,
    fieldName: string,
    runId: string,
    onProgress?: (attempt: number) => void,
    maxAttempts = 120,
    intervalMs = 3000,
  ): Promise<IdeaLandscape> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, attempt === 0 ? 0 : intervalMs));
      onProgress?.(attempt);
      try {
        const res = await api.get(
          `/sub-projects/${subProjectId}/field-update/${fieldName}/run/${runId}`,
        );
        if (res.status === 200) {
          return res.data as IdeaLandscape;
        }
      } catch (err: unknown) {
        if ((err as { response?: { status?: number } })?.response?.status === 202) continue;  // still running
        throw err;
      }
    }
    throw new Error('Field update timed out after waiting for pipeline completion.');
  },

  async rewrite(
    subProjectId: string,
    fieldName: string,
    runId: string,
    data: FieldUpdateRewriteRequest,
  ): Promise<FieldUpdateRewriteResponse> {
    const res = await api.post(
      `/sub-projects/${subProjectId}/field-update/${fieldName}/run/${runId}/rewrite`,
      data,
    );
    return res.data;
  },
};
